"use client";

import type { ScreenCapture } from "@zetalabs/core";
import { motion } from "framer-motion";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import { Action } from "../../action";
import { VStack } from "../../layout/stack";
import { ProgressBar } from "../../loaders/progress-bar";
import { Fullscreen } from "../../microwave/fullscreen";
import { Content, Modal, Trigger } from "../../modal";
import { LiveView } from "../live-view";
import { BaseToolMessage } from "./base";
import type { BaseToolMessageProps } from "./types";

export function WebAgentMessage({
	children,
	text,
	progress,
	statusMessage,
	liveView,
	stepCount,
	isDone,
	isReady,
	isLoading,
	isCanceled = false,
	...props
}: PropsWithChildren<Omit<BaseToolMessageProps, "icon">> & {
	progress: number;
	stepCount?: number;
	liveView?: ScreenCapture[];
	statusMessage?: string;
}): React.ReactElement {
	const [expanded, setExpanded] = useState(!isDone && isReady);

	const toggleExpanded = (): void => {
		setExpanded((prev) => !prev);
	};

	const variants = {
		closed: { height: 0 },
		open: { height: "auto", transition: { staggerChildren: 2 } },
	};

	useEffect(() => {
		if (isReady) {
			setExpanded(true);
		}

		if (isDone || isCanceled) {
			setExpanded(false);
		}
	}, [isDone, isCanceled, isReady]);

	const actions = [];

	if (liveView && liveView.length > 0) {
		actions.push(
			<Modal key="steps">
				<Trigger>
					<Action icon="live" tooltip="Show Live View" />
				</Trigger>
				<Content title="Web Agent" description="View live web agent activity">
					<Fullscreen>
						<LiveView data={liveView} title={text} />
					</Fullscreen>
				</Content>
			</Modal>,
		);
	}

	if (Number(stepCount) > 0) {
		actions.push(
			<Action
				icon="list"
				key="details"
				onClick={toggleExpanded}
				tooltip="Show Details"
			/>,
		);
	}

	return (
		<BaseToolMessage
			actions={actions}
			className="bg-message-webagent-background transition-all"
			icon="ai"
			isCanceled={isCanceled}
			isDone={isDone}
			isLoading={!isReady}
			text={text}
			{...props}
		>
			{expanded ? (
				<VStack asChild className="my-md gap-sm pl-lg">
					<motion.div animate="open" variants={variants}>
						{children}
					</motion.div>
				</VStack>
			) : null}
			{isDone || !isReady || isCanceled ? null : (
				<ProgressBar
					caption={statusMessage}
					isBuffering={isLoading}
					max={1}
					value={progress}
				/>
			)}
		</BaseToolMessage>
	);
}
